import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CoachClaimBanner } from "../components/ClaimBanners";
import { CoachEvalHistory } from "../components/CoachEvalHistory";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Search,
  LogOut,
  BadgeCheck,
  ChevronRight,
  Bell,
  User,
  Filter,
  GraduationCap,
  MapPin,
  Target,
  TrendingUp,
  X,
  Eye,
  BookOpen,
  Award,
  Mail,
  ExternalLink,
  MessageSquare,
  Heart,
  Users,
  Clock,
  History
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NSB Logo Component
import { NSBLogo } from "../components/NSBLogo";

// Position options
const POSITIONS = ["SS", "2B", "3B", "1B", "C", "CF", "LF", "RF", "P", "OF", "IF", "UTIL"];

// Height options
const HEIGHT_OPTIONS = [
  "5'6\"", "5'7\"", "5'8\"", "5'9\"", "5'10\"", "5'11\"", 
  "6'0\"", "6'1\"", "6'2\"", "6'3\"", "6'4\"", "6'5\""
];

// Graduation years
const currentYear = new Date().getFullYear();
const GRAD_YEARS = Array.from({length: 8}, (_, i) => currentYear + i);

export default function CoachDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerEvaluations, setPlayerEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState("search");
  const [interestedPlayers, setInterestedPlayers] = useState([]);
  const [loadingInterested, setLoadingInterested] = useState(false);

  // Search filters
  const [filters, setFilters] = useState({
    positions: [],
    minHeight: "",
    maxHeight: "",
    bats: "",
    throws: "",
    minSixtyTime: "",
    maxSixtyTime: "",
    minExitVelocity: "",
    minGPA: "",
    minACT: "",
    minSAT: "",
    graduationYears: [],
    competitionLevel: "",
    location: "",
  });

  // Mock players data
  const mockPlayers = [
    { athlete_id: "p1", name: "Marcus Johnson", position_primary: "SS", position_secondary: "2B", age_decimal: 16.5, graduation_year: 2027, school: "Central High", location: "Austin, TX", competition_level: "HS", bats: "R", throws: "R", height: "5'11\"", weight: 165, gpa: 3.8, act_score: 28, is_verified: true, latest_metrics: { sixty_time: 6.7, exit_velocity: 89 } },
    { athlete_id: "p2", name: "Tyler Williams", position_primary: "P", age_decimal: 15.8, graduation_year: 2028, school: "Westlake HS", location: "Austin, TX", competition_level: "HS", bats: "R", throws: "R", height: "6'2\"", weight: 175, gpa: 3.5, act_score: 25, is_verified: true, latest_metrics: { fastball_velocity: 82 } },
    { athlete_id: "p3", name: "Jake Thompson", position_primary: "C", age_decimal: 17.2, graduation_year: 2026, school: "Lake Travis", location: "Austin, TX", competition_level: "HS", bats: "L", throws: "R", height: "6'0\"", weight: 185, gpa: 3.2, sat_score: 1180, is_verified: false, latest_metrics: { pop_time: 1.98, exit_velocity: 85 } },
    { athlete_id: "p4", name: "Ryan Martinez", position_primary: "CF", position_secondary: "LF", age_decimal: 16.9, graduation_year: 2027, school: "Anderson HS", location: "Austin, TX", competition_level: "HS", bats: "S", throws: "R", height: "5'10\"", weight: 160, gpa: 3.9, act_score: 30, is_verified: true, latest_metrics: { sixty_time: 6.5, exit_velocity: 87 } },
    { athlete_id: "p5", name: "Chris Anderson", position_primary: "3B", age_decimal: 17.5, graduation_year: 2026, school: "Bowie HS", location: "Austin, TX", competition_level: "HS", bats: "R", throws: "R", height: "6'1\"", weight: 195, gpa: 3.0, sat_score: 1050, is_verified: false, latest_metrics: { sixty_time: 7.0, exit_velocity: 92 } },
    { athlete_id: "p6", name: "David Lee", position_primary: "SS", age_decimal: 15.2, graduation_year: 2029, school: "Round Rock HS", location: "Round Rock, TX", competition_level: "HS", bats: "L", throws: "R", height: "5'9\"", weight: 150, gpa: 4.0, is_verified: true, latest_metrics: { sixty_time: 6.8, exit_velocity: 78 } },
  ];

  useEffect(() => {
    fetchData();
    fetchInterestedPlayers();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const userRes = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers,
      });

      if (!userRes.ok) {
        navigate("/login");
        return;
      }

      setUser(await userRes.json());

      // Fetch players
      const playersRes = await fetch(`${API_URL}/api/players`, {
        credentials: "include",
        headers,
      });

      if (playersRes.ok) {
        const data = await playersRes.json();
        const allPlayers = data.length > 0 ? data : mockPlayers;
        setPlayers(allPlayers);
        setFilteredPlayers(allPlayers);
      } else {
        setPlayers(mockPlayers);
        setFilteredPlayers(mockPlayers);
      }
    } catch (error) {
      console.error("Error:", error);
      setPlayers(mockPlayers);
      setFilteredPlayers(mockPlayers);
    } finally {
      setLoading(false);
    }
  };

  const fetchInterestedPlayers = async () => {
    setLoadingInterested(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/coach/interested-players`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInterestedPlayers(data.interested_players || []);
      }
    } catch (error) {
      console.error("Error fetching interested players:", error);
    } finally {
      setLoadingInterested(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleSearch = async () => {
    setSearching(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/players/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({
          positions: filters.positions.length > 0 ? filters.positions : null,
          min_height: filters.minHeight || null,
          max_height: filters.maxHeight || null,
          bats: filters.bats || null,
          throws: filters.throws || null,
          min_sixty_time: filters.minSixtyTime ? parseFloat(filters.minSixtyTime) : null,
          max_sixty_time: filters.maxSixtyTime ? parseFloat(filters.maxSixtyTime) : null,
          min_exit_velocity: filters.minExitVelocity ? parseFloat(filters.minExitVelocity) : null,
          min_gpa: filters.minGPA ? parseFloat(filters.minGPA) : null,
          min_act: filters.minACT ? parseInt(filters.minACT) : null,
          min_sat: filters.minSAT ? parseInt(filters.minSAT) : null,
          graduation_years: filters.graduationYears.length > 0 ? filters.graduationYears : null,
          competition_level: filters.competitionLevel || null,
          location: filters.location || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // If API returns empty, fallback to local mock data filtering
        if (data.players.length === 0 && players.length > 0) {
          filterPlayersLocally();
        } else {
          setFilteredPlayers(data.players);
          toast.success(`Found ${data.count} players`);
        }
      } else {
        // Fallback to client-side filtering
        filterPlayersLocally();
      }
    } catch (error) {
      // Fallback to client-side filtering
      filterPlayersLocally();
    } finally {
      setSearching(false);
    }
  };

  const filterPlayersLocally = () => {
    let filtered = [...players];

    if (filters.positions.length > 0) {
      filtered = filtered.filter(p => 
        filters.positions.includes(p.position_primary) || 
        filters.positions.includes(p.position_secondary)
      );
    }

    if (filters.bats) {
      filtered = filtered.filter(p => p.bats === filters.bats);
    }

    if (filters.throws) {
      filtered = filtered.filter(p => p.throws === filters.throws);
    }

    if (filters.graduationYears.length > 0) {
      filtered = filtered.filter(p => filters.graduationYears.includes(p.graduation_year));
    }

    if (filters.minGPA) {
      filtered = filtered.filter(p => p.gpa && p.gpa >= parseFloat(filters.minGPA));
    }

    if (filters.minACT) {
      filtered = filtered.filter(p => p.act_score && p.act_score >= parseInt(filters.minACT));
    }

    if (filters.minSAT) {
      filtered = filtered.filter(p => p.sat_score && p.sat_score >= parseInt(filters.minSAT));
    }

    if (filters.minExitVelocity) {
      filtered = filtered.filter(p => 
        p.latest_metrics?.exit_velocity && 
        p.latest_metrics.exit_velocity >= parseFloat(filters.minExitVelocity)
      );
    }

    if (filters.maxSixtyTime) {
      filtered = filtered.filter(p => 
        p.latest_metrics?.sixty_time && 
        p.latest_metrics.sixty_time <= parseFloat(filters.maxSixtyTime)
      );
    }

    if (filters.location) {
      filtered = filtered.filter(p => 
        p.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    setFilteredPlayers(filtered);
    toast.success(`Found ${filtered.length} players`);
  };

  const clearFilters = () => {
    setFilters({
      positions: [],
      minHeight: "",
      maxHeight: "",
      bats: "",
      throws: "",
      minSixtyTime: "",
      maxSixtyTime: "",
      minExitVelocity: "",
      minGPA: "",
      minACT: "",
      minSAT: "",
      graduationYears: [],
      competitionLevel: "",
      location: "",
    });
    setFilteredPlayers(players);
  };

  const togglePosition = (pos) => {
    setFilters(prev => ({
      ...prev,
      positions: prev.positions.includes(pos)
        ? prev.positions.filter(p => p !== pos)
        : [...prev.positions, pos]
    }));
  };

  const toggleGradYear = (year) => {
    setFilters(prev => ({
      ...prev,
      graduationYears: prev.graduationYears.includes(year)
        ? prev.graduationYears.filter(y => y !== year)
        : [...prev.graduationYears, year]
    }));
  };

  const fetchPlayerEvaluations = async (athleteId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/scout/evaluations/${athleteId}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json();
        setPlayerEvaluations(data.evaluations || []);
      } else {
        setPlayerEvaluations([]);
      }
    } catch {
      setPlayerEvaluations([]);
    }
  };

  const viewPlayerDetail = (player) => {
    setSelectedPlayer(player);
    fetchPlayerEvaluations(player.athlete_id);
    setActiveTab("player");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="coach-dashboard">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <NSBLogo className="h-10" light={false} />
            </Link>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-400 hover:text-gray-600">
                <Bell size={20} />
              </button>
              <Button variant="ghost" size="sm" className="text-gray-600" onClick={handleLogout}>
                <LogOut size={16} className="mr-2" /> Sign Out
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Coach Recruiting Portal</h1>
          <p className="text-gray-500 mt-1">Find and evaluate players based on your criteria</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              <GraduationCap size={14} /> Coach Portal
            </span>
          </div>
        </div>

        {/* Coach Claim Banner */}
        <CoachClaimBanner />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-gray-200 rounded-lg p-1 mb-6">
            <TabsTrigger value="search" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md px-4 py-2">
              Player Search
            </TabsTrigger>
            <TabsTrigger value="interested" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md px-4 py-2 relative">
              <Heart size={14} className="mr-1" />
              Interested In You
              {interestedPlayers.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {interestedPlayers.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="player" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md px-4 py-2" disabled={!selectedPlayer}>
              Player Detail
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md px-4 py-2" disabled={!selectedPlayer}>
              <History size={14} className="mr-1" />
              Eval History
            </TabsTrigger>
            <TabsTrigger value="watchlist" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md px-4 py-2">
              My Watchlist
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="mt-0">
            <div className="grid lg:grid-cols-4 gap-6">
              {/* Filter Panel */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Filters</h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X size={14} className="mr-1" /> Clear
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Positions */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Positions</Label>
                    <div className="flex flex-wrap gap-2">
                      {POSITIONS.map(pos => (
                        <button
                          key={pos}
                          onClick={() => togglePosition(pos)}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                            filters.positions.includes(pos)
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Height Range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-500">Min Height</Label>
                      <Select value={filters.minHeight || "any"} onValueChange={(v) => setFilters({...filters, minHeight: v === "any" ? "" : v})}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="any">Any</SelectItem>
                          {HEIGHT_OPTIONS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Max Height</Label>
                      <Select value={filters.maxHeight || "any"} onValueChange={(v) => setFilters({...filters, maxHeight: v === "any" ? "" : v})}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="any">Any</SelectItem>
                          {HEIGHT_OPTIONS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Bats/Throws */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-500">Bats</Label>
                      <Select value={filters.bats || "any"} onValueChange={(v) => setFilters({...filters, bats: v === "any" ? "" : v})}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="R">Right</SelectItem>
                          <SelectItem value="L">Left</SelectItem>
                          <SelectItem value="S">Switch</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Throws</Label>
                      <Select value={filters.throws || "any"} onValueChange={(v) => setFilters({...filters, throws: v === "any" ? "" : v})}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="R">Right</SelectItem>
                          <SelectItem value="L">Left</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Graduation Years */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Grad Year</Label>
                    <div className="flex flex-wrap gap-2">
                      {GRAD_YEARS.slice(0, 6).map(year => (
                        <button
                          key={year}
                          onClick={() => toggleGradYear(year)}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                            filters.graduationYears.includes(year)
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Athletic Metrics */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Athletic Metrics</Label>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-gray-500">Max 60 Time (sec)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="e.g., 6.8"
                          value={filters.maxSixtyTime}
                          onChange={(e) => setFilters({...filters, maxSixtyTime: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Min Exit Velo (mph)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 85"
                          value={filters.minExitVelocity}
                          onChange={(e) => setFilters({...filters, minExitVelocity: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Academic */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Academic</Label>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-gray-500">Min GPA</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="e.g., 3.5"
                          value={filters.minGPA}
                          onChange={(e) => setFilters({...filters, minGPA: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">Min ACT</Label>
                          <Input
                            type="number"
                            placeholder="23"
                            value={filters.minACT}
                            onChange={(e) => setFilters({...filters, minACT: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Min SAT</Label>
                          <Input
                            type="number"
                            placeholder="1100"
                            value={filters.minSAT}
                            onChange={(e) => setFilters({...filters, minSAT: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <Label className="text-xs text-gray-500">Location</Label>
                    <Input
                      placeholder="City, State"
                      value={filters.location}
                      onChange={(e) => setFilters({...filters, location: e.target.value})}
                      className="text-sm"
                    />
                  </div>

                  {/* Search Button */}
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleSearch}
                    disabled={searching}
                  >
                    <Search size={16} className="mr-2" />
                    {searching ? "Searching..." : "Search Players"}
                  </Button>
                </div>
              </div>

              {/* Results */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Results ({filteredPlayers.length} players)</h3>
                  </div>

                  {filteredPlayers.length === 0 ? (
                    <div className="text-center py-12">
                      <Search size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No players match your criteria</p>
                      <p className="text-sm text-gray-400 mt-2">Try adjusting your filters</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {filteredPlayers.map((player) => (
                        <div
                          key={player.athlete_id}
                          className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                          onClick={() => viewPlayerDetail(player)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <User size={24} className="text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900 truncate">{player.name}</h4>
                                {player.is_verified && <BadgeCheck size={14} className="text-blue-500 flex-shrink-0" />}
                              </div>
                              <p className="text-sm text-gray-500">
                                {player.position_primary}{player.position_secondary && `/${player.position_secondary}`} • {player.height} • {player.graduation_year}
                              </p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                <GraduationCap size={12} /> {player.school}
                                <span>•</span>
                                <MapPin size={12} /> {player.location}
                              </div>
                              
                              {/* Metrics Preview */}
                              <div className="flex gap-2 mt-3">
                                {player.latest_metrics?.sixty_time && (
                                  <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium">
                                    60: {player.latest_metrics.sixty_time}s
                                  </span>
                                )}
                                {player.latest_metrics?.exit_velocity && (
                                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                    EV: {player.latest_metrics.exit_velocity}
                                  </span>
                                )}
                                {player.gpa && (
                                  <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium">
                                    GPA: {player.gpa}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="flex-shrink-0">
                              <Eye size={16} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="player" className="mt-0">
            {selectedPlayer && (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Main Content - 2 columns */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Player Header Card */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-start justify-between mb-6 pb-6 border-b">
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center">
                          <span className="text-3xl font-bold text-white">{selectedPlayer.name?.charAt(0)}</span>
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            {selectedPlayer.name}
                            {selectedPlayer.is_verified && <BadgeCheck size={20} className="text-blue-500" />}
                          </h2>
                          <p className="text-gray-500">
                            {selectedPlayer.position_primary}{selectedPlayer.position_secondary && `/${selectedPlayer.position_secondary}`} • 
                            {selectedPlayer.height} • {selectedPlayer.weight} lbs • 
                            B/T: {selectedPlayer.bats}/{selectedPlayer.throws}
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            {selectedPlayer.school} • {selectedPlayer.location} • Class of {selectedPlayer.graduation_year}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/athlete/${selectedPlayer.athlete_id}`} target="_blank">
                          <Button variant="outline" size="sm">
                            <ExternalLink size={14} className="mr-2" /> Public Profile
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm">
                          <Award size={14} className="mr-2" /> Watchlist
                        </Button>
                      </div>
                    </div>

                    {/* Quick Stats Row */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{selectedPlayer.latest_metrics?.sixty_time || "--"}</p>
                        <p className="text-xs text-gray-500 uppercase">60 Time (s)</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{selectedPlayer.latest_metrics?.exit_velocity || "--"}</p>
                        <p className="text-xs text-gray-500 uppercase">Exit Velo</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-900">{selectedPlayer.gpa || "--"}</p>
                        <p className="text-xs text-gray-500 uppercase">GPA</p>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <p className="text-2xl font-bold text-orange-600">{selectedPlayer.age_decimal?.toFixed(1) || "--"}</p>
                        <p className="text-xs text-gray-500 uppercase">Age</p>
                      </div>
                    </div>
                  </div>

                  {/* Full Athletic Metrics */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Target size={18} className="text-blue-500" />
                      Athletic Metrics (Verified)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-500">60-Yard Dash</p>
                        <p className="text-2xl font-bold">{selectedPlayer.latest_metrics?.sixty_time ? `${selectedPlayer.latest_metrics.sixty_time}s` : "N/A"}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-500">Exit Velocity</p>
                        <p className="text-2xl font-bold">{selectedPlayer.latest_metrics?.exit_velocity ? `${selectedPlayer.latest_metrics.exit_velocity} mph` : "N/A"}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-500">Fastball Velocity</p>
                        <p className="text-2xl font-bold">{selectedPlayer.latest_metrics?.fastball_velocity ? `${selectedPlayer.latest_metrics.fastball_velocity} mph` : "N/A"}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-500">Pop Time</p>
                        <p className="text-2xl font-bold">{selectedPlayer.latest_metrics?.pop_time ? `${selectedPlayer.latest_metrics.pop_time}s` : "N/A"}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-500">Bat Speed</p>
                        <p className="text-2xl font-bold">{selectedPlayer.latest_metrics?.bat_speed ? `${selectedPlayer.latest_metrics.bat_speed} mph` : "N/A"}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-500">Infield Velocity</p>
                        <p className="text-2xl font-bold">{selectedPlayer.latest_metrics?.infield_velocity ? `${selectedPlayer.latest_metrics.infield_velocity} mph` : "N/A"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Scout Evaluations - Detailed */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Award size={18} className="text-blue-800" />
                        Scout Evaluations ({playerEvaluations.length})
                      </h3>
                      {playerEvaluations.length > 1 && (
                        <Button variant="outline" size="sm">
                          Compare Evaluations
                        </Button>
                      )}
                    </div>
                    
                    {playerEvaluations.length === 0 ? (
                      <div className="bg-gray-50 rounded-lg p-8 text-center">
                        <Award size={48} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No scout evaluations available yet</p>
                        <p className="text-sm text-gray-400">Attend an NSB combine to get evaluated</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {playerEvaluations.map((evaluation, idx) => (
                          <div key={idx} className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
                              <div>
                                <span className="font-semibold text-gray-900">{evaluation.evaluator_name || "NSB Scout"}</span>
                                <span className="text-sm text-gray-500 ml-2">
                                  {new Date(evaluation.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {evaluation.ppi?.current_composite && (
                                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                                    PPI: {evaluation.ppi.current_composite}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="p-4">
                              {/* Tool Grades */}
                              {evaluation.tool_grades && (
                                <div className="grid grid-cols-5 gap-2 mb-4">
                                  {evaluation.tool_grades.hit_composite && (
                                    <div className="text-center p-2 bg-blue-50 rounded">
                                      <p className="text-lg font-bold text-blue-600">{evaluation.tool_grades.hit_composite}</p>
                                      <p className="text-xs text-gray-500">Hit</p>
                                    </div>
                                  )}
                                  {evaluation.tool_grades.run && (
                                    <div className="text-center p-2 bg-green-50 rounded">
                                      <p className="text-lg font-bold text-green-600">{evaluation.tool_grades.run}</p>
                                      <p className="text-xs text-gray-500">Run</p>
                                    </div>
                                  )}
                                  {evaluation.tool_grades.field && (
                                    <div className="text-center p-2 bg-purple-50 rounded">
                                      <p className="text-lg font-bold text-blue-900">{evaluation.tool_grades.field}</p>
                                      <p className="text-xs text-gray-500">Field</p>
                                    </div>
                                  )}
                                  {evaluation.tool_grades.arm && (
                                    <div className="text-center p-2 bg-orange-50 rounded">
                                      <p className="text-lg font-bold text-orange-600">{evaluation.tool_grades.arm}</p>
                                      <p className="text-xs text-gray-500">Arm</p>
                                    </div>
                                  )}
                                  {(evaluation.tool_grades.hit_for_power || evaluation.tool_grades.raw_power) && (
                                    <div className="text-center p-2 bg-red-50 rounded">
                                      <p className="text-lg font-bold text-red-600">{evaluation.tool_grades.hit_for_power || evaluation.tool_grades.raw_power}</p>
                                      <p className="text-xs text-gray-500">Power</p>
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Notes */}
                              {evaluation.written_notes && (
                                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                                  <p className="text-sm text-gray-700 italic">&ldquo;{evaluation.written_notes}&rdquo;</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar - 1 column */}
                <div className="space-y-6">
                  {/* Academic Info */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <BookOpen size={18} className="text-green-500" />
                      Academic Profile
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-500">GPA</span>
                        <span className="font-bold text-lg">{selectedPlayer.gpa || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-500">ACT Score</span>
                        <span className="font-bold text-lg">{selectedPlayer.act_score || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-500">SAT Score</span>
                        <span className="font-bold text-lg">{selectedPlayer.sat_score || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-500">Graduation</span>
                        <span className="font-bold text-lg">{selectedPlayer.graduation_year}</span>
                      </div>
                    </div>
                  </div>

                  {/* Physical Profile */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Physical Profile</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Height</span>
                        <span className="font-semibold">{selectedPlayer.height || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Weight</span>
                        <span className="font-semibold">{selectedPlayer.weight ? `${selectedPlayer.weight} lbs` : "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Bats</span>
                        <span className="font-semibold">{selectedPlayer.bats || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Throws</span>
                        <span className="font-semibold">{selectedPlayer.throws || "N/A"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Recruiting Notes (Coach Only) */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <MessageSquare size={18} className="text-yellow-500" />
                      My Recruiting Notes
                    </h3>
                    <textarea 
                      className="w-full p-3 border rounded-lg text-sm resize-none" 
                      rows={4}
                      placeholder="Add private notes about this recruit..."
                    />
                    <Button variant="outline" className="w-full mt-3" size="sm">
                      Save Notes
                    </Button>
                  </div>

                  {/* Contact Actions */}
                  <div className="bg-gradient-to-br from-blue-500 to-blue-900 rounded-xl p-6 text-white">
                    <h3 className="font-bold mb-2">Ready to Recruit?</h3>
                    <p className="text-sm text-white/80 mb-4">
                      Reach out to start the recruiting conversation.
                    </p>
                    <Button className="w-full bg-white text-blue-600 hover:bg-white/90">
                      <Mail size={16} className="mr-2" />
                      Contact Athlete
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Interested Players Tab */}
          <TabsContent value="interested" className="mt-0">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Heart className="text-red-500" size={20} />
                    Players Interested In Your Program
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">
                    These athletes have expressed interest in playing for your school
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchInterestedPlayers} disabled={loadingInterested}>
                  {loadingInterested ? "Refreshing..." : "Refresh"}
                </Button>
              </div>

              {loadingInterested ? (
                <div className="text-center py-12 text-gray-500">Loading interested players...</div>
              ) : interestedPlayers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto text-gray-300" size={48} />
                  <p className="text-gray-500 mt-4">No players have expressed interest yet</p>
                  <p className="text-gray-400 text-sm mt-1">When athletes add your school to their Top 20, they&apos;ll appear here</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {interestedPlayers.map((interest) => (
                    <div 
                      key={interest.interest_id} 
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-gray-50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white font-bold">
                            {interest.athlete_name?.charAt(0) || "A"}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{interest.athlete_name || "Unknown"}</h4>
                            <p className="text-sm text-gray-500">
                              {interest.athlete?.position_primary || "Position N/A"} • {interest.athlete?.graduation_year || "Year N/A"}
                            </p>
                          </div>
                        </div>
                        {!interest.coach_viewed && (
                          <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full font-medium">NEW</span>
                        )}
                      </div>

                      {/* Athlete Details */}
                      {interest.athlete && (
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <GraduationCap size={14} />
                            <span>{interest.athlete.school || "School N/A"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin size={14} />
                            <span>{interest.athlete.location || "Location N/A"}</span>
                          </div>
                          {interest.athlete.gpa && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <BookOpen size={14} />
                              <span>GPA: {interest.athlete.gpa}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Interest Note */}
                      {interest.notes && (
                        <div className="bg-blue-50 rounded-lg p-3 mb-4">
                          <p className="text-sm text-blue-800 italic">&ldquo;{interest.notes}&rdquo;</p>
                        </div>
                      )}

                      {/* Tuition Rewards Info */}
                      {interest.school_accepts_tuition_rewards && interest.has_tuition_rewards && (
                        <div className="flex items-center gap-2 text-sm text-green-600 mb-4">
                          <Award size={14} />
                          <span>Has {interest.tuition_points || 0} NSB Tuition Reward Points</span>
                        </div>
                      )}

                      {/* Interest Date */}
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                        <Clock size={12} />
                        <span>Interested since {new Date(interest.created_at).toLocaleDateString()}</span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={() => {
                            setSelectedPlayer(interest.athlete);
                            setActiveTab("player");
                          }}
                          disabled={!interest.athlete}
                        >
                          <Eye size={14} className="mr-1" />
                          View Profile
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={() => navigate(`/athlete/${interest.athlete_id}`)}
                        >
                          <ExternalLink size={14} className="mr-1" />
                          Public Page
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Evaluation History Tab */}
          <TabsContent value="history" className="mt-0">
            {selectedPlayer ? (
              <CoachEvalHistory 
                athleteId={selectedPlayer.athlete_id} 
                athleteName={selectedPlayer.name}
              />
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <History className="mx-auto text-gray-300" size={48} />
                <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">Evaluation History</h3>
                <p className="text-gray-500">Select a player to view their evaluation history and scout comparisons.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="watchlist" className="mt-0">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">My Watchlist</h3>
              <p className="text-gray-500">Players you&apos;re tracking will appear here.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
